import {
  CheckCircleFilled,
  OrderedListOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { Button, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import ModalComponent from '../../../../../../components/ModalComponent';
import {
  GENERATE_MODES,
  INITIAL_ROADMAP_WEEKS,
  ROADMAP_QUESTIONS,
} from '../../../../ultraPlanConstants';
import { getPreferenceCodes } from '../../../../ultraPlanUtils';
import RoadmapQuestionStep from './RoadmapQuestionStep';

const { Text } = Typography;

const MODE_OPTIONS = [
  {
    value: GENERATE_MODES.AUTO,
    icon: <ThunderboltOutlined />,
    title: 'Generate automatically',
    getDescription: () =>
      'Age-suitable, in-stock toys are picked for you. Quickest option.',
  },
  {
    value: GENERATE_MODES.PREFERENCES,
    icon: <OrderedListOutlined />,
    title: 'Based on preferences',
    getDescription: (name) =>
      `Answer ${ROADMAP_QUESTIONS.length} quick questions about ${name} to personalise the toys. All optional.`,
  },
];

const ModeOption = ({ option, childName, isSelected, disabled, onSelect }) => (
  <button
    type="button"
    aria-pressed={isSelected}
    className={`generate-mode-card ${isSelected ? 'is-selected' : ''}`}
    disabled={disabled}
    onClick={onSelect}
  >
    <span className="generate-mode-icon">{option?.icon}</span>
    <Text strong>{option?.title}</Text>
    <Text type="secondary" className="ultra-plan-caption">
      {option?.getDescription(childName)}
    </Text>
    {isSelected && <CheckCircleFilled className="generate-mode-check" />}
  </button>
);

const StepProgress = ({ current, total }) => (
  <div className="generate-progress" aria-hidden>
    {Array.from({ length: total }, (_, index) => (
      <span
        key={index}
        className={`generate-progress-step ${
          index <= current ? 'is-done' : ''
        }`}
      />
    ))}
  </div>
);

const GenerateRoadmapModal = ({
  open,
  childName,
  loading,
  onCancel,
  onGenerate,
}) => {
  const [mode, setMode] = useState(GENERATE_MODES.AUTO);
  const [questionIndex, setQuestionIndex] = useState(null);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!open) return;
    setMode(GENERATE_MODES.AUTO);
    setQuestionIndex(null);
    setAnswers({});
  }, [open]);

  const isQuestionStep = questionIndex !== null;
  const question = ROADMAP_QUESTIONS?.[questionIndex];
  const isLastQuestion = questionIndex === ROADMAP_QUESTIONS.length - 1;
  const questionCodes = answers?.[question?.key] || [];
  const selectedCount = getPreferenceCodes(answers)?.length;

  const handlePrimary = () => {
    if (!isQuestionStep && mode === GENERATE_MODES.AUTO) {
      onGenerate([]);
    } else if (isLastQuestion) {
      onGenerate(getPreferenceCodes(answers));
    } else {
      setQuestionIndex((isQuestionStep ? questionIndex : -1) + 1);
    }
  };

  const handleBack = () =>
    setQuestionIndex(questionIndex > 0 ? questionIndex - 1 : null);

  const getPrimaryLabel = () => {
    if (!isQuestionStep) {
      return mode === GENERATE_MODES.AUTO ? 'Generate Roadmap' : 'Continue';
    }
    if (isLastQuestion) return 'Generate Roadmap';
    return questionCodes?.length ? 'Next' : 'Skip';
  };

  const isGenerateAction =
    (!isQuestionStep && mode === GENERATE_MODES.AUTO) || isLastQuestion;

  const getFooterNote = () => {
    if (!isQuestionStep) {
      return `Weeks 1–${INITIAL_ROADMAP_WEEKS} are filled. Nothing is ordered.`;
    }
    if (!selectedCount) return 'No preferences picked yet';
    return `${selectedCount} preference${selectedCount > 1 ? 's' : ''} picked`;
  };

  const footer = (
    <div className="ultra-plan-modal-footer">
      <Text type="secondary" className="ultra-plan-caption">
        {getFooterNote()}
      </Text>
      <div className="ultra-plan-modal-footer-actions">
        {isQuestionStep ? (
          <Button onClick={handleBack} disabled={loading}>
            Back
          </Button>
        ) : (
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          type="primary"
          icon={isGenerateAction ? <ThunderboltOutlined /> : undefined}
          loading={loading}
          onClick={handlePrimary}
        >
          {getPrimaryLabel()}
        </Button>
      </div>
    </div>
  );

  return (
    <ModalComponent
      open={open}
      width={640}
      wrapClassName="ultra-plan-modal"
      title={
        <div className="ultra-plan-modal-header">
          <span className="ultra-plan-modal-title">
            {`Generate roadmap for ${childName}`}
          </span>
          <Text type="secondary" className="ultra-plan-modal-subtitle">
            {isQuestionStep
              ? `Question ${questionIndex + 1} of ${
                  ROADMAP_QUESTIONS.length
                } · all questions are optional`
              : 'How should toys be picked?'}
          </Text>
        </div>
      }
      footer={footer}
      onCancel={loading ? undefined : onCancel}
      closable={!loading}
      maskClosable={!loading}
      destroyOnClose
    >
      {isQuestionStep ? (
        <>
          <StepProgress
            current={questionIndex}
            total={ROADMAP_QUESTIONS.length}
          />
          <RoadmapQuestionStep
            question={question}
            childName={childName}
            codes={questionCodes}
            disabled={loading}
            onChange={(codes) =>
              setAnswers((prev) => ({ ...prev, [question?.key]: codes }))
            }
          />
        </>
      ) : (
        <div className="generate-mode-options">
          {MODE_OPTIONS?.map((option) => (
            <ModeOption
              key={option?.value}
              option={option}
              childName={childName}
              isSelected={mode === option?.value}
              disabled={loading}
              onSelect={() => setMode(option?.value)}
            />
          ))}
        </div>
      )}
    </ModalComponent>
  );
};

export default GenerateRoadmapModal;
